import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  Autocomplete,
} from "@mui/material";

// ✅ Esquema de validación con Yup
const schema = yup.object().shape({
  pronom: yup.string().max(255, "Máximo 255 caracteres").required("El nombre del proyecto es obligatorio"),
  orgid: yup.number().required("Debe seleccionar una organización"),
});

const ProjectsForm = ({
  open,
  handleClose,
  onSubmit,
  defaultValues = {},
  organizations = [], // Recibimos las organizaciones desde el padre
}) => {
  const isEditing = Boolean(defaultValues?.proid);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      pronom: "",
      orgid: null,
      ...defaultValues,
    },
  });

  // Resetear el formulario cuando cambia `defaultValues` o se abre
  useEffect(() => {
    if (open) {
      // Validar si el orgid existe en la lista de organizaciones
      const isValid = organizations.some((org) => org.orgid === defaultValues.orgid);
      reset({
        ...defaultValues,
        orgid: isValid ? defaultValues.orgid : null,
      });
    }
  }, [open, defaultValues, reset, organizations]);

  // Mapeo para Autocomplete: buscar organización por orgid
  const selectedOrganization = useMemo(() => {
    if (!defaultValues.orgid) return null;
    return organizations.find((org) => org.orgid === defaultValues.orgid) || null;
  }, [organizations, defaultValues.orgid]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? "Editar Proyecto" : "Crear Proyecto"}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {/* Nombre del Proyecto */}
          <TextField
            label="Nombre del Proyecto"
            fullWidth
            margin="normal"
            {...register("pronom")}
            error={!!errors.pronom}
            helperText={errors.pronom?.message}
          />

          {/* Selección de Organización con Autocomplete y búsqueda */}
          <Controller
            name="orgid"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={organizations}
                getOptionLabel={(option) => option.orgnom || ""}
                isOptionEqualToValue={(option, value) => option.orgid === value?.orgid}
                value={selectedOrganization}
                onChange={(_, newValue) => {
                  field.onChange(newValue ? newValue.orgid : null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organización"
                    margin="normal"
                    error={!!errors.orgid}
                    helperText={errors.orgid?.message}
                  />
                )}
                disabled={organizations.length === 0}
                loading={organizations.length === 0}
                noOptionsText="No hay organizaciones disponibles"
                fullWidth
                autoHighlight
                filterOptions={(options, state) => {
                  // Búsqueda por nombre
                  const inputValue = state.inputValue.toLowerCase().trim();
                  return options.filter((option) =>
                    option.orgnom.toLowerCase().includes(inputValue)
                  );
                }}
              />
            )}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" variant="contained">
            {isEditing ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProjectsForm;